import React, { useState, useEffect } from 'react';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../amplify/data/resource';
import StatusMessage from '../components/StatusMessage';



interface Registration {
  id: string;
  courseId: string;
  firstName: string;
  lastName: string;
  email: string;
  company?: string;
  website?: string;
  street?: string;
  houseNumber?: string;
  postalCode?: string;
  city?: string;
  phone?: string;
  registrationDate: string;
  status: string;
  notes?: string;
  specialRequirements?: string;
  registrantFirstName?: string;
  registrantLastName?: string;
  registrantEmail?: string;
  registrantPhone?: string;
  isApproved?: boolean;
  // Invoice-related fields
  invoicingCompany?: string;
  invoicingStreet?: string;
  invoicingHouseNumber?: string;
  invoicingPostalCode?: string;
  invoicingCity?: string;
  pricePerParticipant?: number;
  discount?: number;
}

interface InvoiceGroup {
  company: string;
  invoicingCompany: string;
  invoicingAddress: string;
  courseId: string;
  courseTitle: string;
  registrations: Registration[];
  totalParticipants: number;
  pricePerParticipant: number;
  discount: number;
  totalAmount: number;
  invoiceStatus: 'open' | 'invoiced' | 'paid';
  groupKey: string;
  // New: shared org and invoicing fields for editing
  website?: string;
  street?: string;
  houseNumber?: string;
  postalCode?: string;
  city?: string;
  invoicingStreet?: string;
  invoicingHouseNumber?: string;
  invoicingPostalCode?: string;
  invoicingCity?: string;
  notes?: string;
  specialRequirements?: string;
}

interface AdminInvoiceEditorProps {
  selectedGroupKey?: string;
  onBack?: () => void;
}

const AdminInvoiceEditor: React.FC<AdminInvoiceEditorProps> = ({ selectedGroupKey: propSelectedGroupKey, onBack }) => {
  const [invoiceGroups, setInvoiceGroups] = useState<InvoiceGroup[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<InvoiceGroup | null>(null);
  const [selectedGroupKey, setSelectedGroupKey] = useState<string>(propSelectedGroupKey || '');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'warning' | 'info'; text: string } | null>(null);
  const [editedGroup, setEditedGroup] = useState<InvoiceGroup | null>(null);

  useEffect(() => {
    fetchInvoiceGroups();
  }, []);

  // Handle prop changes and group selection
  useEffect(() => {
    console.log('🎯 AdminInvoiceEditor: Props/Groups changed. propSelectedGroupKey:', propSelectedGroupKey, 'invoiceGroups.length:', invoiceGroups.length);
    
    // Always set the selectedGroupKey from prop first
    if (propSelectedGroupKey && propSelectedGroupKey !== selectedGroupKey) {
      console.log('🔄 AdminInvoiceEditor: Setting selectedGroupKey to:', propSelectedGroupKey);
      setSelectedGroupKey(propSelectedGroupKey);
    }
    
    // Then try to find and select the group if we have both key and groups
    if (propSelectedGroupKey && invoiceGroups.length > 0) {
      console.log('🔍 AdminInvoiceEditor: Looking for group with key:', propSelectedGroupKey);
      console.log('📊 Available groups:', invoiceGroups.map(g => ({ key: g.groupKey, company: g.invoicingCompany, course: g.courseTitle })));
      
      const group = invoiceGroups.find(g => g.groupKey === propSelectedGroupKey);
      if (group) {
        console.log('✅ AdminInvoiceEditor: Found and selecting group:', group.invoicingCompany, '-', group.courseTitle);
        setSelectedGroup(group);
        setEditedGroup({ ...group });
      } else {
        console.log('❌ AdminInvoiceEditor: Group not found for key:', propSelectedGroupKey);
      }
    }
  }, [propSelectedGroupKey, invoiceGroups, selectedGroupKey]);

  const fetchInvoiceGroups = async () => {
    try {
      setLoading(true);
      const client = generateClient<Schema>({ authMode: 'userPool' });

      // Fetch all registrations
      const registrationsResponse = await client.models.Registration.list();
      const allRegistrations = registrationsResponse.data || [];

      // Fetch courses
      const coursesResponse = await client.models.Course.list();
      const courses = coursesResponse.data || [];

      // Create invoice groups
      console.log('📊 AdminInvoiceEditor: Creating groups from', allRegistrations.length, 'registrations and', courses.length, 'courses');
      const groups = createInvoiceGroups(allRegistrations, courses);
      console.log('📋 AdminInvoiceEditor: Created', groups.length, 'invoice groups:', groups.map(g => g.groupKey));
      setInvoiceGroups(groups);
    } catch (error) {
      console.error('Error fetching invoice groups:', error);
      setMessage({ type: 'error', text: 'Failed to load invoice data' });
    } finally {
      setLoading(false);
    }
  };

  const createInvoiceGroups = (registrations: any[], courses: any[]): InvoiceGroup[] => {
    // CRITICAL BUG FIX: Filter out deleted registrations BEFORE grouping
    // This prevents deleted registrations from being merged with new ones
    const activeRegistrations = registrations.filter(reg => reg.status !== 'deleted');
    console.log(`📊 AdminInvoiceEditor: Filtered ${registrations.length} total to ${activeRegistrations.length} active registrations`);
    
    const groupMap = new Map<string, InvoiceGroup>();

    activeRegistrations.forEach((reg: any) => {
      const course = courses.find(c => c.id === reg.courseId);
      if (!course) return;

      // SMART GROUPING STRATEGY:
      // - New registrations: Have exact same timestamp (milliseconds) from our fix
      // - Old registrations: Have timestamps milliseconds apart (need time window grouping)
      // 
      // Solution: Round timestamp to nearest 30 seconds + use registrant email
      // - Groups participants from same form submission (within 30 sec window)
      // - Keeps separate submissions distinct (different times or different registrants)
      // - Prevents accidental merging (different registrants, even if same time)
      let registrationTimeKey = 'no-time';
      if (reg.registrationDate) {
        const timestamp = new Date(reg.registrationDate).getTime();
        // Round to nearest 30 seconds (30000 milliseconds)
        const roundedTimestamp = Math.round(timestamp / 30000) * 30000;
        registrationTimeKey = new Date(roundedTimestamp).toISOString();
      }
      
      // Include registrant email to distinguish different people from same company
      const registrantKey = reg.registrantEmail || reg.email || 'no-registrant';
      const groupKey = `${reg.invoicingCompany || reg.company || 'Unknown'}-${reg.courseId}-${registrantKey}-${registrationTimeKey}`;
      
      if (!groupMap.has(groupKey)) {
        groupMap.set(groupKey, {
          company: reg.company || 'Unknown Company',
          invoicingCompany: reg.invoicingCompany || reg.company || 'Unknown Company',
          invoicingAddress: [
            [reg.invoicingStreet, reg.invoicingHouseNumber].filter(Boolean).join(' '),
            [reg.invoicingPostalCode, reg.invoicingCity].filter(Boolean).join(' ')
          ].filter(Boolean).join(', '),
          courseId: reg.courseId,
          courseTitle: course.title,
          registrations: [],
          totalParticipants: 0,
          pricePerParticipant: reg.pricePerParticipant || 0,
          discount: reg.discount || 0,
          totalAmount: 0,
          invoiceStatus: determineInvoiceStatus(reg.status),
          groupKey,
          // copy shared org fields from first reg
          website: reg.website,
          street: reg.street,
          houseNumber: reg.houseNumber,
          postalCode: reg.postalCode,
          city: reg.city,
          invoicingStreet: reg.invoicingStreet,
          invoicingHouseNumber: reg.invoicingHouseNumber,
          invoicingPostalCode: reg.invoicingPostalCode,
          invoicingCity: reg.invoicingCity,
          notes: reg.notes,
          specialRequirements: reg.specialRequirements
        });
      }

      const group = groupMap.get(groupKey)!;
      group.registrations.push(reg);
      group.totalParticipants = group.registrations.length;
      
      // Calculate total amount
      const subtotal = group.totalParticipants * group.pricePerParticipant;
      group.totalAmount = subtotal - (subtotal * group.discount / 100);
    });

    return Array.from(groupMap.values());
  };

  const determineInvoiceStatus = (registrationStatus: string): 'open' | 'invoiced' | 'paid' => {
    switch (registrationStatus) {
      case 'confirmed': return 'paid';
      case 'invoiced': return 'invoiced';
      case 'approved': return 'open';
      default: return 'open';
    }
  };

  const handleSave = async () => {
    if (!editedGroup || !selectedGroup) return;

    // Prevent double submissions
    if (saving) {
      console.warn('⚠️ Save already in progress, ignoring duplicate submission');
      return;
    }

    try {
      setSaving(true);
      const client = generateClient<Schema>({ authMode: 'userPool' });

      // Update all registrations in the group
      const updatePromises = editedGroup.registrations.map(reg => {
        return client.models.Registration.update({
          id: reg.id,
          invoicingCompany: editedGroup.invoicingCompany,
          // Org/company fields
          company: editedGroup.company,
          website: editedGroup.website,
          street: editedGroup.street,
          houseNumber: editedGroup.houseNumber,
          postalCode: editedGroup.postalCode,
          city: editedGroup.city,
          // Invoicing fields
          invoicingStreet: editedGroup.invoicingStreet,
          invoicingHouseNumber: editedGroup.invoicingHouseNumber,
          invoicingPostalCode: editedGroup.invoicingPostalCode,
          invoicingCity: editedGroup.invoicingCity,
          // Additional info
          notes: editedGroup.notes,
          specialRequirements: editedGroup.specialRequirements,
          pricePerParticipant: editedGroup.pricePerParticipant,
          discount: editedGroup.discount,
          // Update other fields as needed
        });
      });

      await Promise.all(updatePromises);
      
      setMessage({ type: 'success', text: 'Invoice group updated successfully' });
      setSelectedGroup(editedGroup);
      
      // Refresh the data
      await fetchInvoiceGroups();
    } catch (error) {
      console.error('Error updating invoice group:', error);
      setMessage({ type: 'error', text: 'Failed to update invoice group' });
    } finally {
      setSaving(false);
    }
  };

  const handleGroupSelection = (groupKey: string) => {
    setSelectedGroupKey(groupKey);
    if (groupKey && invoiceGroups.length > 0) {
      const group = invoiceGroups.find(g => g.groupKey === groupKey);
      if (group) {
        setSelectedGroup(group);
        setEditedGroup({ ...group });
      }
    } else {
      setSelectedGroup(null);
      setEditedGroup(null);
    }
  };

  const handleFieldChange = (field: keyof InvoiceGroup, value: any) => {
    if (!editedGroup) return;

    const updated = { ...editedGroup, [field]: value };
    
    // Recalculate total amount when price or discount changes
    if (field === 'pricePerParticipant' || field === 'discount') {
      const subtotal = updated.totalParticipants * updated.pricePerParticipant;
      updated.totalAmount = subtotal - (subtotal * updated.discount / 100);
    }
    
    setEditedGroup(updated);
  };

  // Create and manage header selector
  useEffect(() => {
    const container = document.getElementById('header-selector-container');
    if (!container || invoiceGroups.length === 0) return;

    console.log('🎛️ AdminInvoiceEditor: Creating header selector. Groups:', invoiceGroups.length, 'Selected:', selectedGroupKey);
    
    // Create the select element
    const selectHTML = `
      <select
        id="invoiceGroupSelect"
        style="padding: 8px 12px; border: 1px solid rgba(255, 255, 255, 0.3); border-radius: 6px; font-size: 14px; min-width: 300px; background: rgba(255, 255, 255, 0.95); color: #374151; box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);"
      >
        <option value="">Select Invoice Group</option>
        ${invoiceGroups.map(group => 
          `<option value="${group.groupKey}">${group.invoicingCompany} - ${group.courseTitle}</option>`
        ).join('')}
      </select>
    `;
    
    container.innerHTML = selectHTML;
    
    // Get the select element and set up event listener
    const selectElement = container.querySelector('select') as HTMLSelectElement;
    if (selectElement) {
      // Set the selected value if we have one
      if (selectedGroupKey) {
        selectElement.value = selectedGroupKey;
        console.log('🎯 AdminInvoiceEditor: Set initial selector value to:', selectedGroupKey);
      }
      
      // Add change listener
      const handleChange = (e: Event) => {
        const newValue = (e.target as HTMLSelectElement).value;
        console.log('🔄 AdminInvoiceEditor: Selector changed to:', newValue);
        handleGroupSelection(newValue);
      };
      
      selectElement.addEventListener('change', handleChange);
      
      // Store cleanup function
      return () => {
        selectElement.removeEventListener('change', handleChange);
        if (container) {
          container.innerHTML = '';
        }
      };
    }
    
    return () => {
      if (container) {
        container.innerHTML = '';
      }
    };
  }, [invoiceGroups]);

  // Update selector when selectedGroupKey changes
  useEffect(() => {
    const selectElement = document.getElementById('invoiceGroupSelect') as HTMLSelectElement;
    if (selectElement && selectedGroupKey) {
      console.log('🔄 AdminInvoiceEditor: Updating selector from', selectElement.value, 'to:', selectedGroupKey);
      selectElement.value = selectedGroupKey;
      
      // Verify the update worked
      if (selectElement.value === selectedGroupKey) {
        console.log('✅ AdminInvoiceEditor: Selector successfully updated to:', selectedGroupKey);
      } else {
        console.log('❌ AdminInvoiceEditor: Selector update failed. Expected:', selectedGroupKey, 'Got:', selectElement.value);
        // Try again with a small delay
        setTimeout(() => {
          selectElement.value = selectedGroupKey;
          console.log('🔄 AdminInvoiceEditor: Retry selector update to:', selectedGroupKey, 'Result:', selectElement.value);
        }, 100);
      }
    }
  }, [selectedGroupKey]);

  if (loading) {
    return <div className="admin-loading">Loading invoice editor...</div>;
  }

  return (
    <div className="admin-invoice-editor">
      {message && (
        <div style={{ padding: '16px' }}>
          <StatusMessage
            type={message.type}
            message={message.text}
            onClose={() => setMessage(null)}
          />
        </div>
      )}

      {!selectedGroup ? (
        <div className="admin-content-card">
          <div style={{ padding: '48px 24px', textAlign: 'center', color: '#6b7280' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📋</div>
            <h3 style={{ margin: '0 0 8px 0', color: '#374151' }}>Select an Invoice Group</h3>
            <p style={{ margin: 0 }}>
              Use the dropdown in the header to select an invoice group to edit.
            </p>
          </div>
        </div>
      ) : (
        <div className="admin-content-card">

          {editedGroup && (
            <div style={{ padding: '24px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '32px' }}>
                {/* Company Information */}
                <div>
                  <h3 style={{ marginBottom: '16px', color: '#374151' }}>Company Information</h3>
                  
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                      Company Name
                    </label>
                    <input
                      type="text"
                      value={editedGroup.company}
                      onChange={(e) => handleFieldChange('company', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                      Company Website
                    </label>
                    <input
                      type="text"
                      value={editedGroup.website || ''}
                      onChange={(e) => handleFieldChange('website', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '12px', marginBottom: '16px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                        Company Street
                      </label>
                      <input
                        type="text"
                        value={editedGroup.street || ''}
                        onChange={(e) => handleFieldChange('street', e.target.value)}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '14px'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                        House No.
                      </label>
                      <input
                        type="text"
                        value={editedGroup.houseNumber || ''}
                        onChange={(e) => handleFieldChange('houseNumber', e.target.value)}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '14px'
                        }}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '12px', marginBottom: '16px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                        Postal Code
                      </label>
                      <input
                        type="text"
                        value={editedGroup.postalCode || ''}
                        onChange={(e) => handleFieldChange('postalCode', e.target.value)}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '14px'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                        City
                      </label>
                      <input
                        type="text"
                        value={editedGroup.city || ''}
                        onChange={(e) => handleFieldChange('city', e.target.value)}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '14px'
                        }}
                      />
                    </div>
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                      Invoicing Company
                    </label>
                    <input
                      type="text"
                      value={editedGroup.invoicingCompany}
                      onChange={(e) => handleFieldChange('invoicingCompany', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '12px', marginBottom: '16px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                        Invoicing Street
                      </label>
                      <input
                        type="text"
                        value={editedGroup.invoicingStreet || ''}
                        onChange={(e) => handleFieldChange('invoicingStreet', e.target.value)}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '14px'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                        House No.
                      </label>
                      <input
                        type="text"
                        value={editedGroup.invoicingHouseNumber || ''}
                        onChange={(e) => handleFieldChange('invoicingHouseNumber', e.target.value)}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '14px'
                        }}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '12px', marginBottom: '16px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                        Postal Code
                      </label>
                      <input
                        type="text"
                        value={editedGroup.invoicingPostalCode || ''}
                        onChange={(e) => handleFieldChange('invoicingPostalCode', e.target.value)}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '14px'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                        City
                      </label>
                      <input
                        type="text"
                        value={editedGroup.invoicingCity || ''}
                        onChange={(e) => handleFieldChange('invoicingCity', e.target.value)}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '14px'
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Course & Pricing Information */}
                <div>
                  <h3 style={{ marginBottom: '16px', color: '#374151' }}>Course & Pricing</h3>
                  
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                      Course
                    </label>
                    <input
                      type="text"
                      value={editedGroup.courseTitle}
                      readOnly
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        backgroundColor: '#f9fafb',
                        color: '#6b7280'
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                      Total Participants
                    </label>
                    <input
                      type="number"
                      value={editedGroup.totalParticipants}
                      readOnly
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        backgroundColor: '#f9fafb',
                        color: '#6b7280'
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                      Price per Participant (€)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={editedGroup.pricePerParticipant}
                      onChange={(e) => handleFieldChange('pricePerParticipant', parseFloat(e.target.value) || 0)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                      Discount (%)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      max="100"
                      value={editedGroup.discount}
                      onChange={(e) => handleFieldChange('discount', parseFloat(e.target.value) || 0)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                      Total Amount (€)
                    </label>
                    <input
                      type="text"
                      value={`€${editedGroup.totalAmount.toLocaleString()}`}
                      readOnly
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        backgroundColor: '#f0f9ff',
                        color: '#1e40af',
                        fontWeight: 600
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Participants List */}
              <div style={{ marginBottom: '32px' }}>
                <h3 style={{ marginBottom: '16px', color: '#374151' }}>Participants ({editedGroup.totalParticipants})</h3>
                <div style={{ overflowX: 'auto' }}>
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {editedGroup.registrations.map((reg) => (
                        <tr key={reg.id}>
                          <td>{reg.firstName} {reg.lastName}</td>
                          <td>{reg.email}</td>
                          <td>{reg.phone || '-'}</td>
                          <td>
                            <span style={{
                              padding: '4px 8px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: 500,
                              backgroundColor: getRegistrationStatusColor(reg.status).bg,
                              color: getRegistrationStatusColor(reg.status).color
                            }}>
                              {reg.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Additional Information */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
                <div>
                  <h3 style={{ marginBottom: '12px', color: '#374151' }}>Notes</h3>
                  <textarea
                    value={editedGroup.notes || ''}
                    onChange={(e) => handleFieldChange('notes', e.target.value)}
                    rows={4}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      resize: 'vertical'
                    }}
                  />
                </div>
                <div>
                  <h3 style={{ marginBottom: '12px', color: '#374151' }}>Special Requirements</h3>
                  <textarea
                    value={editedGroup.specialRequirements || ''}
                    onChange={(e) => handleFieldChange('specialRequirements', e.target.value)}
                    rows={4}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      resize: 'vertical'
                    }}
                  />
                </div>
              </div>

              {/* Save Button */}
              <div style={{ textAlign: 'right' }}>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  style={{
                    padding: '12px 24px',
                    background: '#008F99',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: saving ? 'not-allowed' : 'pointer',
                    opacity: saving ? 0.6 : 1,
                    transition: 'all 0.2s ease'
                  }}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};



const getRegistrationStatusColor = (status: string) => {
  switch (status) {
    case 'confirmed': return { bg: '#d1fae5', color: '#065f46' };
    case 'pending': return { bg: '#fef3c7', color: '#92400e' };
    case 'cancelled': return { bg: '#fee2e2', color: '#991b1b' };
    case 'alumni': return { bg: '#e0e7ff', color: '#3730a3' };
    case 'invoiced': return { bg: '#dbeafe', color: '#1e40af' };
    case 'approved': return { bg: '#d1fae5', color: '#065f46' };
    default: return { bg: '#f3f4f6', color: '#374151' };
  }
};

export default AdminInvoiceEditor;
